/**
 * Cookie consent for analytics and tracking ([B33]). Under the EU ePrivacy rules and GDPR these tools
 * may run only after the visitor said yes — "by continuing you agree" is not consent. So nothing below
 * loads until the choice is `accepted`:
 * - Google Tag Manager and Hotjar (they used to be inline scripts in public/index.html),
 * - Google Analytics 4 (`vue-gtag` is installed with `bootstrap: false` in main.js),
 * - Mixpanel (initialised with `opt_out_tracking_by_default` in main.js).
 *
 * The choice is kept in localStorage (LOCAL_KEY_TRACKING_CONSENT) and can be changed from the footer.
 * Necessary storage (login session, the choice itself) does not need consent.
 */
import Vue from 'vue'
import { bootstrap as bootstrapGtag } from 'vue-gtag'
import { LOCAL_KEY_TRACKING_CONSENT } from '@/js/constants/storageKeys'

export const ConsentChoice = {
  ACCEPTED: 'accepted',
  DECLINED: 'declined'
}

/** Set only in the environments that should be measured (Cloudflare Pages production). */
const GA_MEASUREMENT_ID = process.env.VUE_APP_GTAG
const GTM_CONTAINER_ID = process.env.VUE_APP_GTM_ID
const HOTJAR_SITE_ID = process.env.VUE_APP_HOTJAR_ID
const MIXPANEL_TOKEN = process.env.VUE_APP_MIXPANEL_TOKEN

const GTM_SCRIPT_URL = 'https://www.googletagmanager.com/gtm.js'
const HOTJAR_SCRIPT_URL = 'https://static.hotjar.com/c/hotjar-'
/** Hotjar snippet version (the `hjsv` of their install code). */
const HOTJAR_SNIPPET_VERSION = 6

function readStoredChoice () {
  try {
    const value = localStorage.getItem(LOCAL_KEY_TRACKING_CONSENT)
    return Object.values(ConsentChoice).includes(value) ? value : null
  } catch (error) {
    // Private mode or blocked storage: treat as "not chosen yet" — nothing loads, the banner shows.
    return null
  }
}

/** Reactive, so the banner and the footer link follow it. */
export const consentState = Vue.observable({
  choice: readStoredChoice(),
  isBannerOpen: false
})
consentState.isBannerOpen = consentState.choice === null

let isTrackingStarted = false

/**
 * Loads every tracking tool. Safe to call twice; runs only once per page load.
 * @param {Object|undefined} mixpanel the app's `$mixpanel`
 */
export function startTracking (mixpanel) {
  if (isTrackingStarted) return
  isTrackingStarted = true
  loadGoogleTagManager()
  loadHotjar()
  if (GA_MEASUREMENT_ID) {
    bootstrapGtag().catch(error => console.error('Google Analytics did not load', error))
  }
  if (MIXPANEL_TOKEN && mixpanel) {
    mixpanel.opt_in_tracking()
  }
}

/**
 * Stores the visitor's choice and acts on it. Going from accepted to declined reloads the page:
 * scripts that already run stop only that way.
 * @param {string} choice one of ConsentChoice
 * @param {Object|undefined} mixpanel the app's `$mixpanel`
 */
export function saveConsentChoice (choice, mixpanel) {
  const wasAccepted = consentState.choice === ConsentChoice.ACCEPTED
  try {
    localStorage.setItem(LOCAL_KEY_TRACKING_CONSENT, choice)
  } catch (error) {
    // The choice still applies to this page view; it will be asked again next time.
    console.warn('Could not store the cookie choice', error)
  }
  consentState.choice = choice
  consentState.isBannerOpen = false

  if (choice === ConsentChoice.ACCEPTED) {
    startTracking(mixpanel)
  } else if (wasAccepted) {
    if (MIXPANEL_TOKEN && mixpanel) {
      mixpanel.opt_out_tracking()
    }
    window.location.reload()
  }
}

/** Shows the banner again (footer "Sīkdatņu iestatījumi"). */
export function openConsentSettings () {
  consentState.isBannerOpen = true
}

function loadGoogleTagManager () {
  if (!GTM_CONTAINER_ID) {
    console.info('Google Tag Manager not loaded: VUE_APP_GTM_ID is not set for this environment')
    return
  }
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
  appendScript(`${GTM_SCRIPT_URL}?id=${encodeURIComponent(GTM_CONTAINER_ID)}`)
}

function loadHotjar () {
  if (!HOTJAR_SITE_ID) {
    console.info('Hotjar not loaded: VUE_APP_HOTJAR_ID is not set for this environment')
    return
  }
  // The queue and settings Hotjar's install code defines before its script arrives.
  window.hj = window.hj || function () { (window.hj.q = window.hj.q || []).push(arguments) }
  window._hjSettings = { hjid: Number(HOTJAR_SITE_ID), hjsv: HOTJAR_SNIPPET_VERSION }
  appendScript(`${HOTJAR_SCRIPT_URL}${HOTJAR_SITE_ID}.js?sv=${HOTJAR_SNIPPET_VERSION}`)
}

function appendScript (src) {
  const script = document.createElement('script')
  script.async = true
  script.src = src
  script.onerror = () => console.error('Tracking script failed to load:', src)
  document.head.appendChild(script)
}
